import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { applyServerErrors, SelectField, TextField } from '../../../components/ui/Field';
import { ErrorState, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../services/http';
import { CATEGORY_LABELS } from '../../platform/api';
import { tenantApi, tenantKeys, type TenantProfile } from '../api';

const optional = z
  .string()
  .trim()
  .max(200)
  .transform((v) => (v === '' ? null : v));
const schema = z.object({
  name: z.string().trim().min(1, 'Enter the business name').max(150),
  legalName: optional,
  businessCategory: z.string(),
  contactEmail: z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .pipe(z.string().email('Enter a valid email').nullable()),
  contactPhone: optional,
  addressLine1: optional,
  city: optional,
  state: optional,
  postalCode: optional,
  countryCode: z.string().regex(/^[A-Z]{2}$/, 'Use a 2-letter country code'),
});
type Values = z.input<typeof schema>;
type Output = z.output<typeof schema>;

function toValues(p: TenantProfile): Values {
  return {
    name: p.name,
    legalName: p.legalName ?? '',
    businessCategory: p.businessCategory,
    contactEmail: p.contactEmail ?? '',
    contactPhone: p.contactPhone ?? '',
    addressLine1: p.addressLine1 ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    postalCode: p.postalCode ?? '',
    countryCode: p.countryCode,
  };
}

/** Business profile form (FR-TEN-04), shared by settings and the onboarding wizard. */
export function ProfileForm({
  submitLabel = 'Save changes',
  onSaved,
}: {
  submitLabel?: string;
  onSaved?: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: tenantKeys.profile, queryFn: tenantApi.profile });
  const form = useForm<Values, unknown, Output>({
    resolver: zodResolver(schema),
    ...(profile.data ? { values: toValues(profile.data) } : {}),
  });
  const save = useMutation({
    mutationFn: (values: Output) => tenantApi.updateProfile(values),
    onSuccess: (data) => {
      queryClient.setQueryData(tenantKeys.profile, data);
      toast.success('Business profile saved');
      onSaved?.();
    },
    onError: (err) =>
      err instanceof ApiError ? applyServerErrors(err.fieldErrors, form.setError as never) : undefined,
  });

  if (profile.isPending) return <TableSkeleton rows={4} />;
  if (profile.isError) return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  const e = form.formState.errors;

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit((v) => save.mutate(v))}
      className="grid gap-4 sm:grid-cols-2"
    >
      {save.error && !(save.error instanceof ApiError && save.error.code === 'VALIDATION_FAILED') ? (
        <div className="sm:col-span-2">
          <ErrorState error={save.error} />
        </div>
      ) : null}
      <TextField label="Business name" required error={e.name?.message} {...form.register('name')} />
      <TextField label="Legal name" error={e.legalName?.message} {...form.register('legalName')} />
      <SelectField
        label="Business type"
        options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
        {...form.register('businessCategory')}
      />
      <TextField
        label="Country code"
        maxLength={2}
        hint="Used to read local phone numbers, e.g. IN"
        error={e.countryCode?.message}
        {...form.register('countryCode')}
      />
      <TextField
        label="Contact email"
        type="email"
        error={e.contactEmail?.message}
        {...form.register('contactEmail')}
      />
      <TextField
        label="Contact phone"
        type="tel"
        error={e.contactPhone?.message}
        {...form.register('contactPhone')}
      />
      <div className="sm:col-span-2">
        <TextField label="Address" error={e.addressLine1?.message} {...form.register('addressLine1')} />
      </div>
      <TextField label="City" {...form.register('city')} />
      <TextField label="State" {...form.register('state')} />
      <TextField label="Postal code" {...form.register('postalCode')} />
      <div className="flex items-end justify-end sm:col-span-2">
        <Button type="submit" loading={save.isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
